import { ReminderOption } from "modules/common/components/authoring/models";
import { NotificationRange } from "modules/documents";
import { NotificationEvents, TenantNotificationSettings } from "modules/messaging";
import { Notifications } from "modules/posts/models";
import { ReminderData } from "modules/settings/components/reminderPopout";

// checks if ANY tenant notifications on post published are enabled (toggle === true)
export const isAnyTenantPostNotificationsEnabled = (notificationSettings: TenantNotificationSettings): boolean => {
    return notificationSettings.settings[NotificationEvents.PostPublishedEmail]?.toggle || 
        notificationSettings.settings[NotificationEvents.PostPublishedMobile]?.toggle || 
        notificationSettings.settings[NotificationEvents.PostPublishedSMS]?.toggle || 
        notificationSettings.settings[NotificationEvents.PostPublishedTeams]?.toggle;
};

// checks if ANY tenant notifications on event published are enabled
export const isAnyTenantEventNotificationsEnabled = (notificationSettings: TenantNotificationSettings): boolean => {
    return notificationSettings.settings[NotificationEvents.EventPublishedEmail]?.toggle || 
        notificationSettings.settings[NotificationEvents.EventPublishedMobile]?.toggle || 
        notificationSettings.settings[NotificationEvents.EventPublishedSMS]?.toggle || 
        notificationSettings.settings[NotificationEvents.EventPublishedTeams]?.toggle;
};

// Get the default NotificationRange tenant setting for all NotificationEvents 
// that's either on post published or event published
export const getDefaultNotificationSetting = (
    event: NotificationEvents, 
    notificationSettings: TenantNotificationSettings
): NotificationRange => {
    let range: NotificationRange = "none";

    if (notificationSettings.settings[event]?.toggle) {
        if (event === NotificationEvents.PostPublishedEmail)
            range = notificationSettings.defaultPostSettings?.emailOnPublish || "none";
        else if (event === NotificationEvents.PostPublishedMobile)
            range = notificationSettings.defaultPostSettings?.mobileOnPublish || "subscribers";
        else if (event === NotificationEvents.PostPublishedSMS)
            range = notificationSettings.defaultPostSettings?.smsOnPublish || "none";
        else if (event === NotificationEvents.PostPublishedTeams)
            range = notificationSettings.defaultPostSettings?.teamsOnPublish || "subscribers";
        else if (event === NotificationEvents.EventPublishedEmail)
            range = notificationSettings.defaultEventSettings?.emailOnPublish || "fallback";
        else if (event === NotificationEvents.EventPublishedMobile)
            range = notificationSettings.defaultEventSettings?.mobileOnPublish || "none";
        else if (event === NotificationEvents.EventPublishedSMS)
            range = notificationSettings.defaultEventSettings?.smsOnPublish || "fallback";
        else if (event === NotificationEvents.EventPublishedTeams)
            range = notificationSettings.defaultEventSettings?.teamsOnPublish || "none";
    }

    return range;
}

// checks if ANY tenant reminders are enabled for posts
export const isAnyTenantPostRemindersEnabled = (notificationSettings: TenantNotificationSettings): boolean => {
    return notificationSettings.settings[NotificationEvents.PostReminderEmail]?.toggle ||
        notificationSettings.settings[NotificationEvents.PostReminderMobile]?.toggle ||
        notificationSettings.settings[NotificationEvents.PostReminderSMS]?.toggle ||
        notificationSettings.settings[NotificationEvents.PostReminderTeams]?.toggle;
};

// checks if ANY tenant reminders are enabled for events
export const isAnyTenantEventRemindersEnabled = (notificationSettings: TenantNotificationSettings): boolean => {
    return notificationSettings.settings[NotificationEvents.EventReminderEmail]?.toggle ||
        notificationSettings.settings[NotificationEvents.EventReminderMobile]?.toggle ||
        notificationSettings.settings[NotificationEvents.EventReminderSMS]?.toggle ||
        notificationSettings.settings[NotificationEvents.EventReminderTeams]?.toggle;
};

export interface ContentTypeNotificationInfo {
    areNotificationsEnabledOnTenant: boolean;
    areRemindersEnabledOnTenant: boolean;
    publishedTeamsKey: NotificationEvents;
    publishedMobileKey: NotificationEvents;
    publishedEmailKey: NotificationEvents;
    publishedSMSKey: NotificationEvents;
    reminderTeamsKey: NotificationEvents;
    reminderMobileKey: NotificationEvents;
    reminderEmailKey: NotificationEvents;
    reminderSMSKey: NotificationEvents;
}

// resolves the event-vs-post NotificationEvents keys and tenant enablement for a content type,
// so callers don't need to branch on isEvent for every channel individually
export const getContentTypeNotificationInfo = (isEvent: boolean, notificationSettings: TenantNotificationSettings): ContentTypeNotificationInfo => ({
    areNotificationsEnabledOnTenant: isEvent
        ? isAnyTenantEventNotificationsEnabled(notificationSettings)
        : isAnyTenantPostNotificationsEnabled(notificationSettings),
    areRemindersEnabledOnTenant: isEvent
        ? isAnyTenantEventRemindersEnabled(notificationSettings)
        : isAnyTenantPostRemindersEnabled(notificationSettings),
    publishedTeamsKey: isEvent ? NotificationEvents.EventPublishedTeams : NotificationEvents.PostPublishedTeams,
    publishedMobileKey: isEvent ? NotificationEvents.EventPublishedMobile : NotificationEvents.PostPublishedMobile,
    publishedEmailKey: isEvent ? NotificationEvents.EventPublishedEmail : NotificationEvents.PostPublishedEmail,
    publishedSMSKey: isEvent ? NotificationEvents.EventPublishedSMS : NotificationEvents.PostPublishedSMS,
    reminderTeamsKey: isEvent ? NotificationEvents.EventReminderTeams : NotificationEvents.PostReminderTeams,
    reminderMobileKey: isEvent ? NotificationEvents.EventReminderMobile : NotificationEvents.PostReminderMobile,
    reminderEmailKey: isEvent ? NotificationEvents.EventReminderEmail : NotificationEvents.PostReminderEmail,
    reminderSMSKey: isEvent ? NotificationEvents.EventReminderSMS : NotificationEvents.PostReminderSMS,
});

/**
 * Checks if at least 1 **enabled** publishing notifications channel is turned on
 * @param notificationSettings the tenant's notifications
 * @param publishName event or post settings name; either defaultEventSettings or defaultPostSettings
 * @returns true if 1 channel is turned on, false otherwise.
 */
export const isAtLeastOnePublishNotifChannelOn = (
    notificationSettings: TenantNotificationSettings, 
    publishName: keyof TenantNotificationSettings
): boolean => {
    const defaultNotifications = notificationSettings[publishName] as Notifications;
    const settings = notificationSettings.settings;

    // check if we're doing posts or event publish notifications
    const isPostSettings = publishName.toLowerCase().includes("post");
    
    // get the default values
    const emailEnabled = settings[isPostSettings ? NotificationEvents.PostPublishedEmail : NotificationEvents.EventPublishedEmail].toggle;
    const emailDefaultIsSet = defaultNotifications.emailOnPublish !== 'none';

    const smsEnabled = settings[isPostSettings ? NotificationEvents.PostPublishedSMS : NotificationEvents.EventPublishedSMS].toggle;
    const smsDefaultIsSet = defaultNotifications.smsOnPublish !== 'none';

    const teamsEnabled = settings[isPostSettings ? NotificationEvents.PostPublishedTeams : NotificationEvents.EventPublishedTeams].toggle;
    const teamsDefaultIsSet = defaultNotifications.teamsOnPublish !== 'none';

    const mobileEnabled = settings[isPostSettings ? NotificationEvents.PostPublishedMobile : NotificationEvents.EventPublishedMobile].toggle;
    const mobileDefaultIsSet = defaultNotifications.mobileOnPublish !== 'none';

    // false unless one channel is found to be on
    let isAtLeastOneChannelOn = false;

    // if the channel is enabled, check if its default is set
    if (emailEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || emailDefaultIsSet;
    }
    
    if (smsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || smsDefaultIsSet;
    }
    
    if (teamsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || teamsDefaultIsSet;
    }
    
    if (mobileEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || mobileDefaultIsSet;
    }
    
    return isAtLeastOneChannelOn;
}

/**
 * Checks if at least 1 **enabled** reminder notifications channel is turned on for a single reminder
 * @param reminderData the reminder default setting data
 * @param notificationSettings the tenant's notifications
 * @param reminderName event or post settings name; either defaultEventSettings or defaultPostSettings
 * @returns true if 1 channel is turned on, false otherwise.
 */
export const isAtLeastOneReminderDataChannelOn = (
    reminderData: ReminderData,
    notificationSettings: TenantNotificationSettings, 
    reminderName: keyof TenantNotificationSettings
): boolean => {
    const settings = notificationSettings.settings;

    // check if we're doing posts or event publish notifications
    const isPostSettings = reminderName.toLowerCase().includes("post");
    
    // get the default values
    const emailEnabled = settings[isPostSettings ? NotificationEvents.PostReminderEmail : NotificationEvents.EventReminderEmail].toggle;
    const emailDefaultIsSet = reminderData.email;

    const smsEnabled = settings[isPostSettings ? NotificationEvents.PostReminderSMS : NotificationEvents.EventReminderSMS].toggle;
    const smsDefaultIsSet =  reminderData.sms;

    const teamsEnabled = settings[isPostSettings ? NotificationEvents.PostReminderTeams : NotificationEvents.EventReminderTeams].toggle;
    const teamsDefaultIsSet = reminderData.teams;

    const mobileEnabled = settings[isPostSettings ? NotificationEvents.PostReminderMobile : NotificationEvents.EventReminderMobile].toggle;
    const mobileDefaultIsSet = reminderData.mobile;
    
    // if inherit, then it will be true. otherwise we check each channel to see if the default is set
    let isAtLeastOneChannelOn = reminderData.inherit;

    // if the channel is enabled, check if its default is set
    if (emailEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || emailDefaultIsSet;
    }
    
    if (smsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || smsDefaultIsSet;
    }
    
    if (teamsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || teamsDefaultIsSet;
    }
    
    if (mobileEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || mobileDefaultIsSet;
    }
    
    return isAtLeastOneChannelOn;
}

/**
 * Checks if at least 1 **enabled** reminder notifications channel is turned on for all reminders
 * @param notificationSettings the tenant's notifications
 * @param reminderName event or post settings name; either defaultEventSettings or defaultPostSettings
 * @returns true if 1 channel is turned on, false otherwise.
 */
export const isAtLeastOneReminderChannelOn = (
    notificationSettings: TenantNotificationSettings, 
    reminderName: keyof TenantNotificationSettings
): boolean => {
    const reminders = (notificationSettings[reminderName] as Notifications).reminders;
    const settings = notificationSettings.settings;

    // check if we're doing posts or event publish notifications
    const isPostSettings = reminderName.toLowerCase().includes("post");
    
    // get the default values
    const emailEnabled = settings[isPostSettings ? NotificationEvents.PostReminderEmail : NotificationEvents.EventReminderEmail].toggle;
    const emailDefaultIsSet = reminders.filter(reminder => reminder.channel === 'email' && reminder.range !== 'none').length > 0;

    const smsEnabled = settings[isPostSettings ? NotificationEvents.PostReminderSMS : NotificationEvents.EventReminderSMS].toggle;
    const smsDefaultIsSet =  reminders.filter(reminder => reminder.channel === 'sms' && reminder.range !== 'none').length > 0;

    const teamsEnabled = settings[isPostSettings ? NotificationEvents.PostReminderTeams : NotificationEvents.EventReminderTeams].toggle;
    const teamsDefaultIsSet = reminders.filter(reminder => reminder.channel === 'teams' && reminder.range !== 'none').length > 0;

    const mobileEnabled = settings[isPostSettings ? NotificationEvents.PostReminderMobile : NotificationEvents.EventReminderMobile].toggle;
    const mobileDefaultIsSet = reminders.filter(reminder => reminder.channel === 'mobile' && reminder.range !== 'none').length > 0;
    
    // if there is an inherit, then it will be true. otherwise we check each channel to see if the default is set
    let isAtLeastOneChannelOn = reminders.filter(reminder => reminder.channel === 'inherit').length > 0;

    // if the channel is enabled, check if its default is set
    if (emailEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || emailDefaultIsSet;
    }
    
    if (smsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || smsDefaultIsSet;
    }
    
    if (teamsEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || teamsDefaultIsSet;
    }
    
    if (mobileEnabled) {
        isAtLeastOneChannelOn = isAtLeastOneChannelOn || mobileDefaultIsSet;
    }
    
    return isAtLeastOneChannelOn;
}

/**
 * a check for if the reminder option is selected to be a custom channel and all the custom channels are off
 * 
 * !isCustomChannelAndNoReminderOptionsAreOn will check that for custom channels (reminderOption.customChannels === true), there are notification 
 * ranges set for at least 1 of the custom channels. If custom channel is not selected (reminderOption.customChannels === false), then it means 
 * we're using the notification ranges from publishing and we don't care if any custom reminder channels are selected.
 */
export const isCustomChannelAndNoReminderOptionsAreOn = (reminderOption: ReminderOption): boolean => {
    return reminderOption.customChannels && !isAnyCustomReminderOptionOn(reminderOption);
}

export const isAnyCustomReminderOptionOn = (reminderOption: ReminderOption): boolean => {
    return (reminderOption.emailRange !== "none" || reminderOption.teamsRange !== "none" || reminderOption.smsRange !== "none" || reminderOption.mobileRange !== "none");
}

export const isAnyCustomNotificationOptionOn = (notifications?: Notifications): boolean => {
    if (!notifications) return false;
    
    const { emailOnPublish, mobileOnPublish, smsOnPublish, teamsOnPublish} = notifications

    return emailOnPublish !== "none" || mobileOnPublish !== "none" || smsOnPublish !== "none" || teamsOnPublish !== "none";
}

export const getNotificationChannelsStrings = (notifications?: Notifications): string[] => {
    let channelStrings: string[] = [];

    if (notifications?.emailOnPublish === "subscribers") channelStrings.push("Email");

    if (notifications?.mobileOnPublish === "subscribers") channelStrings.push("Mobile app");

    if (notifications?.teamsOnPublish === "subscribers") channelStrings.push("MS Teams");

    if (notifications?.smsOnPublish === "subscribers") channelStrings.push("SMS");

    return channelStrings;
}

export const eventUppercase = (eventToFix: string) => {
    if(eventToFix !== null && eventToFix.length > 1)
        return eventToFix[0].toUpperCase() + eventToFix.slice(1);
    else
        return "Invalid string to fix."
}